import React, { useEffect, useMemo, useState } from "react";
import { useCreate } from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import { AutoComplete, Form, Input, Modal, Radio, message } from "antd";
import { debounce } from "lodash";

import type {
  ManualTaskCreateRequest,
  SearchResult,
  TaskResponse,
} from "../../../api-client";
import { UsersService } from "../../../api-client";
import { ImageUpload } from "./ImageUpload";

export interface CreateTaskModalProps {
  campaignId: string;
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultTemplate?: string;
  campaignImageData?: string | null;
}

type CreateTaskFormValues = {
  user_id: string;
  message_text: string;
  image_source: "campaign" | "upload" | "none";
  image_data?: string | null;
};

export const CreateTaskModal: React.FC<CreateTaskModalProps> = (props) => {
  const { campaignId, visible, onClose, onSuccess, defaultTemplate, campaignImageData } =
    props;
  const [form] = Form.useForm<CreateTaskFormValues>();
  const { mutate: createTask, isLoading } =
    useCreate<TaskResponse, HttpError, ManualTaskCreateRequest>();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchOptions, setSearchOptions] = useState<
    { value: string; label: React.ReactNode; data: SearchResult }[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const formatSearchResult = (result: SearchResult) => ({
    value: result.telegram_id.toString(),
    data: result,
    label: (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span>
          {result.username ? `@${result.username}` : "Без username"} ({result.telegram_id})
        </span>
        <span style={{ color: "#8c8c8c", fontSize: 12 }}>
          Баланс: {result.balance}
        </span>
      </div>
    ),
  });

  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchOptions([]);
      return;
    }

    setSearchLoading(true);

    try {
      const response = await UsersService.searchUsersApiUsersSearchGet({
        q: query.trim(),
        limit: 8,
      });

      if (response.results && response.results.length > 0) {
        setSearchOptions(response.results.map(formatSearchResult));
      } else {
        setSearchOptions([
          {
            value: "",
            label: `Пользователи не найдены по запросу "${query}"`,
            data: {} as SearchResult,
          },
        ]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchOptions([
        {
          value: "",
          label: "Ошибка поиска. Попробуйте снова.",
          data: {} as SearchResult,
        },
      ]);
    } finally {
      setSearchLoading(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        void searchUsers(query);
      }, 300),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSubmit = (values: CreateTaskFormValues) => {
    form.setFields([{ name: "user_id", errors: [] }]);

    const imageSource = (values.image_source === "upload"
      ? "task"
      : values.image_source) as ManualTaskCreateRequest["image_source"];
    const imageData =
      values.image_source === "campaign"
        ? campaignImageData ?? null
        : values.image_source === "upload"
          ? values.image_data
          : null;

    // user_id должен быть в формате "user:telegram_id" для API
    const userId = values.user_id.startsWith("user:")
      ? values.user_id
      : `user:${values.user_id}`;

    createTask(
      {
        resource: "campaign-tasks",
        values: {
          user_id: userId,
          message_text: values.message_text,
          image_source: imageSource,
          image_data: imageData,
        },
        meta: {
          url: `/api/campaigns/${campaignId}/tasks`,
        },
      },
      {
        onSuccess: () => {
          messageApi.success("Задача создана");
          onSuccess?.();
          form.resetFields();
          onClose();
        },
        onError: (error) => {
          const status = error?.statusCode ?? error?.response?.status;
          const apiMessage =
            error?.response?.data?.detail ?? error?.message ??
            "Не удалось создать задачу";

          if (status === 404) {
            form.setFields([
              { name: "user_id", errors: ["Пользователь не найден"] },
            ]);
            return;
          }

          if (status === 400) {
            messageApi.error(apiMessage);
            return;
          }

          if (status && status >= 500) {
            messageApi.error("Произошла ошибка сервера. Попробуйте позже.");
            return;
          }

          messageApi.error(apiMessage);
        },
      },
    );
  };

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        message_text: defaultTemplate ?? "",
        image_source: campaignImageData ? "campaign" : "none",
      });
      setSearchOptions([]);
    }
  }, [campaignImageData, defaultTemplate, form, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, visible]);

  return (
    <Modal
      title="Создать задачу вручную"
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Создать задачу"
      confirmLoading={isLoading}
      destroyOnClose
      width={640}
    >
      {contextHolder}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="user_id"
          label="Telegram ID или Username"
          rules={[
            { required: true, message: "Укажите Telegram ID или username" },
          ]}
        >
          <AutoComplete
            options={searchOptions}
            onSelect={(value) => form.setFieldsValue({ user_id: value })}
            onSearch={(value) => {
              form.setFieldsValue({ user_id: value });

              if (!value.trim()) {
                setSearchOptions([]);
                return;
              }

              debouncedSearch(value);
            }}
            onClear={() => setSearchOptions([])}
            notFoundContent={searchLoading ? "Поиск пользователя..." : null}
            filterOption={false}
            allowClear
          >
            <Input
              placeholder="Введите Telegram ID или username"
              aria-label="Telegram ID или Username"
            />
          </AutoComplete>
        </Form.Item>

        <Form.Item
          name="message_text"
          label="Текст сообщения"
          rules={[{ required: true, message: "Введите текст сообщения" }]}
        >
          <Input.TextArea
            rows={4}
            maxLength={2000}
            placeholder="Введите текст сообщения"
          />
        </Form.Item>

        <Form.Item name="image_source" label="Источник изображения">
          <Radio.Group>
            <Radio value="campaign">Использовать изображение кампании</Radio>
            <Radio value="upload">Загрузить новое изображение</Radio>
            <Radio value="none">Без изображения</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) =>
            getFieldValue("image_source") === "upload" ? (
              <Form.Item name="image_data" label="Изображение">
                <ImageUpload />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTaskModal;
